import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft, Upload, Loader2, Download, Mic, Music, CheckCircle2, XCircle, RotateCcw, Film, FileAudio, Trash2, Crown, Clock,
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// TeckiA Voice Kar : une vidéo ou un audio -> 2 MP3 (voix seule + ambiance/musique).
// 10 séparations gratuites par mois ; illimité avec l'abonnement 20 000 Ar / 30 jours (MVola ou Orange Money,
// déclaration de paiement puis validation par l'équipe). Traitement sur le serveur.
// ============================================================

const token = () => localStorage.getItem('auth_token');
const ACCEPT = 'audio/*,video/*,.mp3,.wav,.ogg,.oga,.opus,.m4a,.aac,.flac,.wma,.aiff,.amr,.mp4,.mov,.mkv,.avi,.webm,.m4v,.3gp,.wmv,.flv,.mpeg,.mpg,.ts';
const MAX_MB = 400;

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(b / 1024))} Ko`);
const fmtDur = (s) => {
  if (!s && s !== 0) return '';
  const m = Math.floor(s / 60);
  return `${m} min ${String(Math.round(s % 60)).padStart(2, '0')} s`;
};

const VoiceSplit = ({ onBack }) => {
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);   // 0-100 pendant l'envoi, null sinon
  const [error, setError] = useState('');
  const [drag, setDrag] = useState(false);
  const [urls, setUrls] = useState({});               // { vocals: blobUrl, background: blobUrl }
  const [sub, setSub] = useState(null);             // statut quota / abonnement renvoyé par le serveur
  const [showSub, setShowSub] = useState(false);
  const [payMethod, setPayMethod] = useState('mvola');
  const [payRef, setPayRef] = useState('');
  const [paying, setPaying] = useState(false);
  const [payMsg, setPayMsg] = useState({ type: '', text: '' });
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  useEffect(() => () => stopPoll(), []);
  useEffect(() => () => { Object.values(urls).forEach(u => URL.revokeObjectURL(u)); }, [urls]);

  const authFetch = (path, opts = {}) =>
    fetch(`${API_URL}${path}`, { ...opts, headers: { Authorization: `Bearer ${token()}`, ...(opts.headers || {}) } });

  const loadStatus = useCallback(async () => {
    try {
      const r = await authFetch('/api/voice-split/status');
      const d = await r.json();
      if (d.success) setSub(d);
    } catch (_) { /* affichage du quota indisponible : sans gravité */ }
  }, []);

  const loadResultBlobs = useCallback(async (j) => {
    const out = {};
    for (const kind of ['vocals', 'background']) {
      try {
        const r = await authFetch(`/api/voice-split/jobs/${j.id}/download/${kind}`);
        if (r.ok) out[kind] = URL.createObjectURL(await r.blob());
      } catch (_) { /* le bouton Télécharger réessaiera */ }
    }
    setUrls(out);
  }, []);

  const watch = useCallback((j) => {
    stopPoll();
    setJob(j);
    if (j.status === 'done') { loadResultBlobs(j); return; }
    if (j.status === 'error') return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await authFetch(`/api/voice-split/jobs/${j.id}`);
        const d = await r.json();
        if (!d.success) { stopPoll(); setError(d.message || 'Tâche introuvable'); return; }
        setJob(d.job);
        if (d.job.status === 'done') { stopPoll(); loadResultBlobs(d.job); loadStatus(); }
        if (d.job.status === 'error') { stopPoll(); loadStatus(); }
      } catch (_) { /* coupure réseau passagère : on réessaie au prochain tour */ }
    }, 2000);
  }, [loadResultBlobs, loadStatus]);

  // à l'ouverture : retrouver la dernière séparation (en cours ou terminée)
  useEffect(() => {
    loadStatus();
    (async () => {
      try {
        const r = await authFetch('/api/voice-split/latest');
        const d = await r.json();
        if (d.success && d.job) watch(d.job);
      } catch (_) { /* rien à reprendre */ }
    })();
  }, [watch, loadStatus]);

  const pick = (f) => {
    setError('');
    if (!f) return;
    if (f.size > MAX_MB * 1048576) { setError(`Fichier trop volumineux (maximum ${MAX_MB} Mo).`); return; }
    setFile(f);
  };

  const start = () => {
    if (!file) return;
    setError('');
    setUploadPct(0);
    const form = new FormData();
    form.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_URL}/api/voice-split/jobs`);
    xhr.setRequestHeader('Authorization', `Bearer ${token()}`);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) setUploadPct(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      setUploadPct(null);
      let d = {};
      try { d = JSON.parse(xhr.responseText); } catch (_) { /* réponse non JSON */ }
      if (xhr.status >= 200 && xhr.status < 300 && d.success) { setFile(null); setUrls({}); watch(d.job); loadStatus(); }
      else {
        setError(d.message || 'Envoi impossible. Vérifiez votre connexion et réessayez.');
        if (d.code === 'quota') { setShowSub(true); loadStatus(); }
      }
    };
    xhr.onerror = () => { setUploadPct(null); setError('Envoi impossible. Vérifiez votre connexion et réessayez.'); };
    xhr.send(form);
  };

  const reset = async () => {
    stopPoll();
    const old = job;
    setJob(null); setFile(null); setUrls({}); setError('');
    if (old && (old.status === 'done' || old.status === 'error')) {
      authFetch(`/api/voice-split/jobs/${old.id}`, { method: 'DELETE' }).catch(() => {});
    }
  };

  const download = async (kind) => {
    const name = job.files?.[kind]?.name || `${kind}.mp3`;
    let href = urls[kind];
    if (!href) {
      const r = await authFetch(`/api/voice-split/jobs/${job.id}/download/${kind}`);
      if (!r.ok) { setError('Fichier expiré. Relancez la séparation.'); return; }
      href = URL.createObjectURL(await r.blob());
    }
    const a = document.createElement('a');
    a.href = href; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  };

  const submitPayment = async () => {
    setPayMsg({ type: '', text: '' });
    setPaying(true);
    try {
      const r = await authFetch('/api/voice-split/subscribe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ method: payMethod, payment_ref: payRef.trim() }),
      });
      const d = await r.json();
      if (d.success) {
        setPayMsg({ type: 'ok', text: "Paiement déclaré ! Notre équipe le vérifie ; vous recevrez un message dans votre boîte de réception dès que l'abonnement sera activé." });
        setPayRef(''); setShowSub(false); setError(''); loadStatus();
      } else setPayMsg({ type: 'error', text: d.message || 'Déclaration impossible.' });
    } catch (_) {
      setPayMsg({ type: 'error', text: 'Connexion impossible. Réessayez.' });
    } finally { setPaying(false); }
  };

  const running = job && ['queued', 'extracting', 'separating', 'encoding'].includes(job.status);
  const done = job && job.status === 'done';
  const failed = job && job.status === 'error';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 text-white">
      <div className="max-w-3xl mx-auto px-4 py-6 md:py-10">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={onBack} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2.5">
              <span className="w-10 h-10 rounded-2xl bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center shadow-lg shadow-rose-500/20">
                <Mic className="w-5 h-5" />
              </span>
              Voice Kar
            </h1>
            <p className="text-sm text-blue-200 mt-0.5">Sépare la voix et l'ambiance d'une vidéo ou d'un audio</p>
          </div>
        </div>

        {/* statut : quota gratuit / abonnement */}
        {sub && (
          <div className="mb-5">
            {sub.premium ? (
              <div className="flex items-center gap-2 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-sm text-emerald-200">
                <Crown className="w-4 h-4" /> Illimité jusqu'au <b>{sub.premium_until_label}</b>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-sm">
                <span className={sub.remaining === 0 ? 'text-amber-300' : 'text-blue-100'}>
                  Gratuit : <b>{sub.remaining}</b> / {sub.free_limit} restante{sub.remaining > 1 ? 's' : ''} ce mois-ci
                </span>
                {sub.pending_order ? (
                  <span className="flex items-center gap-1.5 text-amber-300"><Clock className="w-4 h-4" /> Paiement en cours de vérification</span>
                ) : (
                  <button onClick={() => setShowSub(v => !v)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 font-semibold hover:opacity-90 transition">
                    <Crown className="w-4 h-4" /> Illimité · {sub.price_ar.toLocaleString('fr-FR')} Ar / mois
                  </button>
                )}
              </div>
            )}

            {showSub && !sub.premium && !sub.pending_order && (
              <div className="mt-3 rounded-2xl bg-white/5 border border-amber-500/30 p-5 space-y-4">
                <div>
                  <p className="font-bold flex items-center gap-2"><Crown className="w-4 h-4 text-amber-300" /> Abonnement illimité — {sub.price_ar.toLocaleString('fr-FR')} Ar pour {sub.days} jours</p>
                  <p className="text-xs text-blue-200 mt-1">Autant de séparations que vous voulez. Paiement par MVola ou Orange Money.</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(sub.payment).map(([key, info]) => (
                    <button key={key} onClick={() => setPayMethod(key)}
                      className={`py-2.5 rounded-xl border text-sm font-semibold transition ${payMethod === key ? 'bg-amber-500/25 border-amber-400' : 'bg-white/5 border-white/10 hover:border-white/30'}`}>
                      {info.label}
                    </button>
                  ))}
                </div>
                <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-3 text-xs text-amber-100 leading-relaxed">
                  1. Envoyez <b>{sub.price_ar.toLocaleString('fr-FR')} Ar</b> par {sub.payment[payMethod].label} au <b>{sub.payment[payMethod].number}</b> (au nom de <b>{sub.payment[payMethod].name}</b>).<br />
                  2. Recopiez ci-dessous la référence de la transaction reçue par SMS.<br />
                  3. L'équipe vérifie et active votre abonnement ; vous êtes prévenu dans votre boîte de réception.
                </div>
                <input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="Référence de la transaction"
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-sm placeholder-gray-500 focus:outline-none focus:border-amber-400" />
                <button onClick={submitPayment} disabled={paying || payRef.trim().length < 4}
                  className="w-full py-3 rounded-xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 disabled:opacity-40 transition flex items-center justify-center gap-2">
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Déclarer mon paiement
                </button>
              </div>
            )}
            {payMsg.text && (
              <p className={`mt-3 text-sm rounded-xl px-4 py-3 border ${payMsg.type === 'ok' ? 'text-emerald-200 bg-emerald-500/10 border-emerald-500/30' : 'text-rose-300 bg-rose-500/10 border-rose-500/30'}`}>{payMsg.text}</p>
            )}
          </div>
        )}

        {/* 1) choix du fichier */}
        {!job && (
          <div className="space-y-4">
            <div
              onClick={() => uploadPct === null && inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files?.[0]); }}
              className={`cursor-pointer rounded-3xl border-2 border-dashed p-8 md:p-12 text-center transition ${
                drag ? 'border-rose-400 bg-rose-500/10' : 'border-white/20 bg-white/5 hover:bg-white/10'}`}
            >
              <input ref={inputRef} type="file" accept={ACCEPT} className="hidden"
                onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  {file.type.startsWith('video') ? <Film className="w-10 h-10 text-rose-300" /> : <FileAudio className="w-10 h-10 text-amber-300" />}
                  <p className="font-semibold break-all">{file.name}</p>
                  <p className="text-sm text-blue-200">{fmtSize(file.size)} — cliquez pour changer</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-10 h-10 text-rose-300" />
                  <p className="font-semibold">Glissez votre vidéo ou audio ici</p>
                  <p className="text-sm text-blue-200">ou cliquez pour choisir un fichier</p>
                  <p className="text-xs text-gray-400 mt-2">Vidéo : mp4, mov, mkv, avi, webm… · Audio : mp3, wav, ogg, m4a, flac…<br />Maximum {MAX_MB} Mo et 30 minutes par fichier</p>
                </div>
              )}
            </div>

            {uploadPct !== null && (
              <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                <p className="text-sm mb-2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Envoi du fichier… {uploadPct}%</p>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-rose-500 to-amber-500 transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
              </div>
            )}

            <button onClick={start} disabled={!file || uploadPct !== null}
              className="w-full py-3.5 rounded-2xl font-bold bg-gradient-to-r from-rose-500 to-amber-500 hover:opacity-90 disabled:opacity-40 transition flex items-center justify-center gap-2">
              <Mic className="w-5 h-5" /> Séparer la voix et l'ambiance
            </button>
          </div>
        )}

        {error && <p className="mt-4 text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3">{error}</p>}

        {/* 2) traitement en cours */}
        {running && (
          <div className="rounded-3xl bg-white/5 border border-white/10 p-6">
            <p className="font-semibold break-all mb-1">{job.original_name}</p>
            <p className="text-xs text-blue-200 mb-4">{job.kind === 'video' ? 'Vidéo' : 'Audio'}{job.duration_s ? ` · ${fmtDur(job.duration_s)}` : ''}</p>
            <p className="text-sm mb-2 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {job.stage_label} {job.progress}%</p>
            <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-rose-500 to-amber-500 transition-all duration-700" style={{ width: `${job.progress}%` }} />
            </div>
            <p className="text-xs text-gray-400 mt-4">Vous pouvez quitter cette page : le résultat sera retrouvé ici à votre retour (conservé 6 heures).</p>
          </div>
        )}

        {/* 3) erreur de traitement */}
        {failed && (
          <div className="rounded-3xl bg-rose-500/10 border border-rose-500/30 p-6 text-center">
            <XCircle className="w-10 h-10 text-rose-300 mx-auto mb-2" />
            <p className="font-semibold mb-1">La séparation a échoué</p>
            <p className="text-sm text-rose-200 mb-4">{job.error}</p>
            <button onClick={reset} className="px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition inline-flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> Réessayer avec un fichier
            </button>
          </div>
        )}

        {/* 4) résultat */}
        {done && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-300">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold break-all">Terminé — {job.original_name}</span>
            </div>
            {[
              { kind: 'vocals', title: 'Voix seule', hint: 'Uniquement la voix, sans musique ni bruits', Icon: Mic, tone: 'from-rose-500 to-pink-500' },
              { kind: 'background', title: 'Ambiance & musique', hint: 'Musique, bruits et fond sonore, sans la voix', Icon: Music, tone: 'from-amber-500 to-orange-500' },
            ].map(({ kind, title, hint, Icon, tone }) => (
              <div key={kind} className="rounded-3xl bg-white/5 border border-white/10 p-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`w-11 h-11 shrink-0 rounded-2xl bg-gradient-to-br ${tone} flex items-center justify-center`}><Icon className="w-5 h-5" /></span>
                    <div className="min-w-0">
                      <p className="font-bold">{title}</p>
                      <p className="text-xs text-blue-200">{hint}{job.files?.[kind] ? ` · ${job.files[kind].size_mb} Mo` : ''}</p>
                    </div>
                  </div>
                  <button onClick={() => download(kind)}
                    className="shrink-0 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 transition flex items-center gap-2 text-sm font-semibold">
                    <Download className="w-4 h-4" /> <span className="hidden sm:inline">Télécharger</span> MP3
                  </button>
                </div>
                {urls[kind]
                  ? <audio controls src={urls[kind]} className="w-full" />
                  : <p className="text-xs text-gray-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Préparation de l'écoute…</p>}
              </div>
            ))}
            <div className="flex gap-3">
              <button onClick={reset} className="flex-1 py-3 rounded-2xl bg-white/10 hover:bg-white/20 transition font-semibold flex items-center justify-center gap-2">
                <RotateCcw className="w-4 h-4" /> Nouvelle séparation
              </button>
            </div>
            <p className="text-xs text-gray-400 flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Vos fichiers sont supprimés automatiquement du serveur après 6 heures.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceSplit;
